import { useQueryClient } from '@tanstack/react-query';

import type { SplitCategoryTreeResponse } from '@/api/gen/_model/splitCategoryTreeResponse';
import type { SuccessBodySplitCategoryTreeResponse } from '@/api/gen/_model/successBodySplitCategoryTreeResponse';
import {
  getGetCategoryTreesByCountryQueryKey,
  useGetCategoryTreesByCountry,
} from '@/api/gen/category-tree/category-tree.gen';
import {
  getZepetofulFetchSchoolContentQueryKey,
  useZepetofulFetchSchoolContent,
} from '@/api/zepetoful.api';
import { useCountryCode } from '@/shared/hooks';
import {
  type PublisherInfo,
  type SelectionSchoolInfo,
} from '@/views/build/type';

export const useFetchCategoryByWithAuthUser =
  (): ReturnUseCachedCategoryInfo => {
    const { countryCode, isKR, isLoading } = useCountryCode();

    const { data } = useGetCategoryTreesByCountry(countryCode, {
      query: {
        enabled: !isLoading,
      },
    });

    const { data: publishersData } = useZepetofulFetchSchoolContent({
      enabled: isKR && !isLoading,
    });

    return {
      countryCode,
      categoryTreeData: data?.data,
      publishersData: publishersData?.publishers?.KR,
    };
  };

export const useFetchCategoryInfoByCountryCode = (
  countryCode: string,
): ReturnUseCachedCategoryInfo => {
  const { data } = useGetCategoryTreesByCountry(countryCode, {});

  const { data: publishersData } = useZepetofulFetchSchoolContent({
    enabled: countryCode === 'KR',
  });

  return {
    countryCode,
    categoryTreeData: data?.data,
    publishersData: publishersData?.publishers?.KR,
  };
};

// 페이지 최상단에서 호출되어있고 캐싱된 데이터를 사용하고 싶은 케이스
export const useCachedCategoryInfo = (): ReturnUseCachedCategoryInfo => {
  const client = useQueryClient();
  const { countryCode } = useCountryCode();

  const categoryTreeData =
    client.getQueryData<SuccessBodySplitCategoryTreeResponse>(
      getGetCategoryTreesByCountryQueryKey(countryCode),
    )?.data;

  return {
    countryCode,
    categoryTreeData,
    publishersData: client.getQueryData<SelectionSchoolInfo>(
      getZepetofulFetchSchoolContentQueryKey(),
    )?.publishers?.KR,
  };
};

export type ReturnUseCachedCategoryInfo = {
  countryCode: string;
  publishersData?: PublisherInfo[];
  categoryTreeData?: SplitCategoryTreeResponse;
};
